import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { resolveJerseyPair, type Team } from '../utils/jersey-resolver';
import { AnswerButton } from './shared/AnswerButton';

interface PropPickProps {
  teams: Team[];
  question: string;
  subtext?: string;
  selectedAnswer?: 'yes' | 'no';
  onAnswerSelect: (answer: 'yes' | 'no') => void;
  points: string | number;
  earnedPoints?: number;
  percentages?: { yes: number; no: number };
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionId?: string;
  questionNumber?: number;
  thirdImages?: { yes?: string; no?: string };
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

const PropPick: React.FC<PropPickProps> = ({
  teams,
  question,
  subtext,
  selectedAnswer,
  onAnswerSelect,
  points,
  earnedPoints,
  percentages,
  isCorrect,
  disabled = false,
  className = '',
  questionId,
  questionNumber,
  thirdImages,
  questionMetadata,
}) => {
  const { t } = useTranslation();
  // Safely access first two teams for matchup display
  const team1 = teams[0];
  const team2 = teams[1];

  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseys = useMemo(() => {
    if (team1 && team2) {
      return resolveJerseyPair(team1, team2);
    }
    return { team1Jersey: '', team2Jersey: '' };
  }, [team1, team2]);

  const isDailyDouble = questionMetadata?.dailyDouble || false;

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : 'prop-pick'}
      data-testtype="prop"
    >
      {/* Team Matchup */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <TeamJersey size="lg" jerseyUrl={teamJerseys.team1Jersey} />
        <span className="text-white text-center font-gilroy text-2xl font-bold leading-8">
          {t('game.labels.vs')}
        </span>
        <TeamJersey size="lg" jerseyUrl={teamJerseys.team2Jersey} />
      </div>

      {/* Team Names or Subtext */}
      <div className="text-center mb-4">
        <span
          data-testid="prop-matchup"
          className="text-[#ABABAB] text-center font-gilroy text-base font-light leading-5"
        >
          {subtext || (team1 && team2 ? `${team1.name} @ ${team2.name}` : '')}
        </span>
      </div>

      {/* Question */}
      <div className="text-center mb-4">
        <span
          data-testid="prop-question"
          className="text-white text-center font-gilroy text-base font-bold leading-5"
        >
          {question}
        </span>
      </div>

      {/* Answer Options */}
      <div className="grid grid-cols-2 gap-4">
        <AnswerButton
          answer="yes"
          label={t('game.labels.yes')}
          isSelected={selectedAnswer === 'yes'}
          percentage={percentages?.yes}
          onClick={() => onAnswerSelect('yes')}
          disabled={disabled}
          isCorrect={selectedAnswer === 'yes' ? isCorrect : undefined}
          testId={questionId ? `option-${questionId}-yes` : 'prop-answer-yes'}
          thirdImage={thirdImages?.yes}
          isDailyDouble={isDailyDouble}
        />
        <AnswerButton
          answer="no"
          label={t('game.labels.no')}
          isSelected={selectedAnswer === 'no'}
          percentage={percentages?.no}
          onClick={() => onAnswerSelect('no')}
          disabled={disabled}
          isCorrect={selectedAnswer === 'no' ? isCorrect : undefined}
          testId={questionId ? `option-${questionId}-no` : 'prop-answer-no'}
          thirdImage={thirdImages?.no}
          isDailyDouble={isDailyDouble}
        />
      </div>
    </QuestionCard>
  );
};

export default PropPick;
